<template>
    <v-layout no-gutters class="stacked field__phone-number">
        <v-flex :xs8="!hideNumberType">
            <edit-string
                :ref="`${refPrefix}.number`"
                v-model="number"
                :mask="maskValue"
                form
                :disabled="disabled"
                :required="required"
                :rules="rulesValue"
                :error-messages="errorMessagesValue"
                :label="labelValue || $t('phone_number')"
                class="field__phone-number__string"
                :autocomplete="false"
            >
                <template #prepend-inner>
                    <switch-country
                        :ref="`${refPrefix}.country`"
                        v-model="country"
                        hide-button-text
                        return-object
                        required
                    />
                </template>
            </edit-string>
        </v-flex>
        <v-flex v-if="!hideNumberType" xs4>
            <field-lookup
                :ref="`${refPrefix}.type_id`"
                :value="phone_number_type_id"
                type="contact-number-type"
                :disabled="disabled"
                :label="$t('label')"
                class="field__phone-number__label"
                :required="required && requireLabel"
                @input:object="
                    inputValue = new PhoneNumber({
                        ...inputValue,
                        type_id: $event && 'id' in $event ? $event.id : null,
                        number_type: $event && 'name' in $event ? $event.name : null
                    })
                "
            />
        </v-flex>
    </v-layout>
</template>

<script>
import BaseField from './BaseField';
import { PhoneNumber } from '@/models';

export default {
    name: 'PhoneNumber',
    components: {
        SwitchCountry: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/SwitchCountry'
            )
    },
    extends: BaseField,
    props: {
        hideNumberType: {
            type: Boolean,
            default: false
        },
        requireLabel: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        PhoneNumber
    }),
    computed: {
        maskValue() {
            return this.getMask('phoneNumber', {
                prefix: this.inputValue.prefix
            });
        },
        rulesValue() {
            const rules = [];

            if (this.required) {
                rules.push(() => this.getValidator('required')(this.inputValue.number, this.labelValue));
            }

            rules.push(() => this.getValidator('phoneNumber')(this.inputValue.number, this.labelValue));

            return [...this.rules, ...this.rulesArray, ...rules];
        },
        inputValue: {
            get() {
                if (this.value instanceof PhoneNumber) {
                    return this.value;
                }

                if (this.value && typeof this.value === 'object') {
                    return new PhoneNumber(this.value);
                }

                return new PhoneNumber({
                    country: 'GB'
                });
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        number: {
            get() {
                if (this.inputValue.number) {
                    return this.inputValue.number.replaceAll(' ', '');
                }
                return null;
            },
            set(value) {
                value = value ? value.replaceAll(' ', '') : value;
                this.inputValue = new PhoneNumber({ ...this.inputValue, number: value });
            }
        },
        country: {
            get() {
                return this.inputValue.country;
            },
            set(value) {
                let country = null;
                let prefix = null;

                if (value && 'alpha2Code' in value && 'callingCode' in value) {
                    country = value.alpha2Code;
                    prefix = `+${value.callingCode}`;
                }

                this.inputValue = new PhoneNumber({ ...this.inputValue, country, prefix });
            }
        },
        phone_number_type_id: {
            get() {
                return this.inputValue.type_id;
            },
            set(value) {
                this.inputValue = new PhoneNumber({ ...this.inputValue, type_id: value });
            }
        },
        phone_number_type_name: {
            get() {
                return this.inputValue.number_type;
            },
            set(value) {
                this.inputValue = new PhoneNumber({ ...this.inputValue, number_type: value });
            }
        }
    }
};
</script>

<style lang="scss">
.v-application .field__phone-number__string {
    .v-input__slot {
        border-radius: 4px 0 0 4px;
    }
    .v-input__prepend-inner {
        width: 5rem;
        padding-right: 0 !important;
    }
    .v-text-field--outlined.v-input--dense .v-label--active {
        left: -4.85rem !important;
    }
    .v-text-field--rounded > .v-input__control > .v-input__slot {
        padding: 0;
    }
}

.v-application .field__phone-number__label {
    .v-input__slot {
        border-radius: 0 4px 4px 0;
    }
}
</style>
